import { useTranslation } from '@eti/providers';
import { price as priceUtil } from '@eti/utils';
import { Text, textVariants } from '@etg/wings';
import { getAgeTypeLabel, getAgeTypeLabelForQuantity } from '../../../../utils/travelers';
import { CartRow, Price } from '../../../../widgets/cart/components/CartComponents';

const isShowingPrice = (price: number) => Boolean(price);

interface PriceBreakdownTravelerGroupProps {
  ageType: string;
  marketingCarrierNames: string;
  markup: number;
  price: number;
  quantity: number;
  totalPrice: number;
  vat: number;
}

const PriceBreakdownTravelerGroup = ({
  ageType,
  marketingCarrierNames,
  markup,
  price,
  quantity,
  totalPrice,
  vat,
}: PriceBreakdownTravelerGroupProps) => {
  const { t } = useTranslation();
  const travelerTypeLabel = getAgeTypeLabelForQuantity(ageType, quantity);
  const ageTypeLabel = t(getAgeTypeLabel(ageType)).toLowerCase();
  const costLabel = t('Cart.CostPerAgeType', ageTypeLabel);
  const discountLabel = t('Cart.DiscountPerAgeType', ageTypeLabel);
  const travelerTypeCount = t(travelerTypeLabel, quantity);

  let vatLabels = t('Cart.TaxesAndChargesPerAgeType', ageTypeLabel);
  if (marketingCarrierNames) {
    vatLabels = t(
      'Cart.TaxesAndChargesPerAgeType.WithCarriers',
      ageTypeLabel,
      marketingCarrierNames,
    );
  }

  const priceByAgeLabels = t('Cart.PricePerAgeType', ageTypeLabel);
  const markupByAgeLabels = markup >= 0 ? costLabel : discountLabel;

  return (
    <div>
      <CartRow>
        <Text
          data-testid={`trip-priceBreakdown-ageTypeCount-${ageType}`}
          variant={textVariants._14_BOLD}
        >
          {travelerTypeCount}
        </Text>
      </CartRow>
      <CartRow>
        <Text data-testid={`trip-priceBreakdown-pricePerAgeType-${ageType}-label`}>
          {priceByAgeLabels}
        </Text>
        <Price data-testid={`trip-priceBreakdown-pricePerAgeType-${ageType}`}>
          {priceUtil.formatPrice(price)}
        </Price>
      </CartRow>
      {isShowingPrice(vat) && (
        <CartRow>
          <Text data-testid={`trip-priceBreakdown-vat-${ageType}-label`}>{vatLabels}</Text>
          <Price data-testid={`trip-priceBreakdown-vat-${ageType}`}>
            {priceUtil.formatPrice(vat)}
          </Price>
        </CartRow>
      )}
      {isShowingPrice(markup) && (
        <CartRow>
          <Text data-testid={`trip-priceBreakdown-markup-${ageType}-label`}>
            {markupByAgeLabels}
          </Text>
          <Price data-testid={`trip-priceBreakdown-markup-${ageType}`}>
            {priceUtil.formatPrice(markup)}
          </Price>
        </CartRow>
      )}
      <CartRow>
        <Text
          data-testid={`trip-priceBreakdown-totalForAgeType-${ageType}-label`}
          variant={textVariants._14_BOLD}
        >
          {t('TripDetails.PriceBreakdown.TravelerGroupPriceSummary', travelerTypeCount)}
        </Text>
        <Price data-testid={`trip-priceBreakdown-totalForAgeType-${ageType}`}>
          {priceUtil.formatPrice(totalPrice)}
        </Price>
      </CartRow>
    </div>
  );
};

export default PriceBreakdownTravelerGroup;
